import { z } from 'zod'
import { oc } from '@orpc/contract'
import { billingCaseDtoSchema } from './update-case.contract'
import { caseDetailPaymentSchema } from './case-detail.contract'

// In-drawer insurance VCC charge (accounting-plan Task 16): Stripe Elements collects the
// card on the frontend, the backend confirms one PaymentIntent server-side. MOTO is a
// per-charge opt-in (`useMoto`, default false — the "Process as MOTO" switch); the
// default path fails cleanly on a 3DS-demanding card (error_on_requires_action) and the
// attempt is recorded as a retryable failed payment, surfaced as a 422 with the
// failure reason. There is no overpayment confirmation for VCC — an over-expected
// amount is a 409 before any charge.
export const vccChargeInputSchema = z
  .object({
    encounterId: z.string(),
    /** Stripe PaymentMethod id from Elements — card data never touches the middleware. */
    paymentMethodId: z.string().min(1),
    /** Allowed amount from THIS payment's EOB — the charge is allowed − patient_resp. */
    allowedCents: z.number().int().min(0),
    /** Patient responsibility from THIS payment's EOB. */
    patientRespCents: z.number().int().min(0),
    payerName: z.string().min(1),
    /** Calendar date (YYYY-MM-DD), from the EOB. */
    paymentDate: z.iso.date(),
    externalRef: z.string().min(1).optional(),
    /** DocumentReference id of the EOB PDF. */
    eobDocumentId: z.string().optional(),
    /** Per-charge MOTO opt-in, default false ("Process as MOTO" switch ON → true). */
    useMoto: z.boolean().optional(),
  })
  .superRefine((value, ctx) => {
    if (value.allowedCents - value.patientRespCents <= 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['allowedCents'],
        message: 'The derived charge amount (allowed − patient responsibility) must be positive',
      })
    }
  })

export const billingV2VccCharge = oc
  .route({ method: 'POST', path: '/v2/billing/vcc/charge' })
  .input(vccChargeInputSchema)
  .output(
    z.union([
      z.object({
        payment: caseDetailPaymentSchema,
        case: billingCaseDtoSchema,
        /** Non-blocking notices (e.g. an overpayment raced in after the charge). */
        warnings: z.array(z.string()),
      }),
      // The org's Stripe account has no MOTO enablement (only reachable with useMoto):
      // the UI shows the Dashboard-charge + record-here fallback.
      z.object({ motoUnavailable: z.literal(true), message: z.string() }),
    ]),
  )
